import { useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/ui/components'
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components'
import { Badge } from '@/ui/components'
import { Input } from '@/ui/components/primitives/input'
import { Label } from '@/ui/components/primitives/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components/primitives/select'
import { useAuth } from '@/modules/auth'
import {
  createAdminApi,
  type SubscriptionTier,
  type AdminTrip,
  type AdminWaypoint,
  type AdminTripMeal,
  type AdminTripAnnouncement,
  type AdminTripGear,
} from '@/shared/api/admin'
import { PageLayout } from '@/ui/layout'
import { ChevronDown, ChevronRight, MapPin, UtensilsCrossed, Megaphone, Backpack } from '@/ui/icons'

export default function AdminPage() {
  const { user, getToken } = useAuth()
  const [userIdInput, setUserIdInput] = useState('')
  const [targetUserId, setTargetUserId] = useState<string | null>(null)
  const [targetUserInfo, setTargetUserInfo] = useState<{
    id: string
    email: string
    full_name: string | null
    created_at: string | null
    profile: {
      experience_level: string
      bio: string | null
      location: string | null
      years_overlanding: number
      profile_image_path: string | null
      created_at: string
      updated_at: string
    } | null
  } | null>(null)
  const [trips, setTrips] = useState<AdminTrip[]>([])
  const [subscription, setSubscription] = useState<{
    tier: SubscriptionTier
    limits: { tripLimit: number; crewLimit: number; vehicleLimit: number; photoLimit: number }
    features: Record<string, boolean>
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [updatingTier, setUpdatingTier] = useState(false)
  const [waypointsByTripId, setWaypointsByTripId] = useState<Record<string, AdminWaypoint[]>>({})
  const [mealsByTripId, setMealsByTripId] = useState<Record<string, AdminTripMeal[]>>({})
  const [announcementsByTripId, setAnnouncementsByTripId] = useState<Record<string, AdminTripAnnouncement[]>>({})
  const [gearByTripId, setGearByTripId] = useState<Record<string, AdminTripGear[]>>({})
  const [expandedTripId, setExpandedTripId] = useState<string | null>(null)
  const [loadingDetailsTripId, setLoadingDetailsTripId] = useState<string | null>(null)
  const [tripIdInput, setTripIdInput] = useState('')
  const [tripById, setTripById] = useState<AdminTrip | null>(null)
  const [loadingTripById, setLoadingTripById] = useState(false)
  const [tripByIdError, setTripByIdError] = useState<string | null>(null)

  const adminApi = useMemo(() => createAdminApi(getToken), [getToken])

  const loadUser = useCallback(async () => {
    const uid = userIdInput.trim()
    if (!uid) {
      setError('Enter a user ID')
      return
    }
    setError(null)
    setTargetUserId(uid)
    setTargetUserInfo(null)
    setLoading(true)
    try {
      const [userRes, subRes, tripsRes] = await Promise.all([
        adminApi.getUser(uid),
        adminApi.getUserSubscription(uid),
        adminApi.getUserTrips(uid, { limit: 50, include_archived: true }),
      ])
      setTargetUserInfo(userRes.data)
      setSubscription(subRes.data)
      setTrips(Array.isArray(tripsRes.data) ? tripsRes.data : [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load user')
      setTargetUserInfo(null)
      setSubscription(null)
      setTrips([])
    } finally {
      setLoading(false)
    }
  }, [userIdInput, adminApi])

  const handleTierChange = useCallback(
    async (tier: SubscriptionTier) => {
      if (!targetUserId || !subscription) return
      setUpdatingTier(true)
      try {
        await adminApi.updateUserSubscription(targetUserId, { tier })
        const res = await adminApi.getUserSubscription(targetUserId)
        setSubscription(res.data)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update subscription')
      } finally {
        setUpdatingTier(false)
      }
    },
    [targetUserId, subscription, adminApi]
  )

  const toggleTripDetails = useCallback(
    async (tripId: string) => {
      if (expandedTripId === tripId) {
        setExpandedTripId(null)
        return
      }
      const hasDetails = waypointsByTripId[tripId] !== undefined
      if (hasDetails) {
        setExpandedTripId(tripId)
        return
      }
      setLoadingDetailsTripId(tripId)
      try {
        const [waypointsRes, mealsRes, announcementsRes, gearRes] = await Promise.all([
          adminApi.getTripWaypoints(tripId),
          adminApi.getTripMeals(tripId),
          adminApi.getTripAnnouncements(tripId),
          adminApi.getTripGear(tripId),
        ])
        setWaypointsByTripId((prev) => ({ ...prev, [tripId]: Array.isArray(waypointsRes.data) ? waypointsRes.data : [] }))
        setMealsByTripId((prev) => ({ ...prev, [tripId]: Array.isArray(mealsRes.data) ? mealsRes.data : [] }))
        setAnnouncementsByTripId((prev) => ({ ...prev, [tripId]: Array.isArray(announcementsRes.data) ? announcementsRes.data : [] }))
        setGearByTripId((prev) => ({ ...prev, [tripId]: Array.isArray(gearRes.data) ? gearRes.data : [] }))
        setExpandedTripId(tripId)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load trip details')
      } finally {
        setLoadingDetailsTripId(null)
      }
    },
    [adminApi, expandedTripId, waypointsByTripId]
  )

  const loadTripById = useCallback(async () => {
    const id = tripIdInput.trim()
    if (!id) {
      setTripByIdError('Enter a trip ID')
      return
    }
    setTripByIdError(null)
    setTripById(null)
    setLoadingTripById(true)
    try {
      const res = await adminApi.getTrip(id)
      setTripById(res.data)
    } catch (e) {
      setTripById(null)
      setTripByIdError(e instanceof Error ? e.message : 'Failed to load trip')
    } finally {
      setLoadingTripById(false)
    }
  }, [tripIdInput, adminApi])

  const tripLookupList = tripById ? [tripById] : []

  return (
    <PageLayout>
      <div className="container max-w-4xl py-8 space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Admin</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage subscriptions and view user data. Logged in as {user?.email}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>User lookup</CardTitle>
            <p className="text-sm text-muted-foreground">
              Enter a user ID (UUID) to view trips and subscription.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 space-y-2">
              <Label htmlFor="admin-user-id">User ID</Label>
              <Input
                id="admin-user-id"
                placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadUser()}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={loadUser} disabled={loading}>
                {loading ? 'Loading…' : 'Load user'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Trip lookup</CardTitle>
            <p className="text-sm text-muted-foreground">
              Enter a trip ID (UUID) to view trip details and waypoints.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 space-y-2">
              <Label htmlFor="admin-trip-id">Trip ID</Label>
              <Input
                id="admin-trip-id"
                placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000"
                value={tripIdInput}
                onChange={(e) => setTripIdInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadTripById()}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={loadTripById} disabled={loadingTripById}>
                {loadingTripById ? 'Loading…' : 'Load trip'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="rounded-md bg-destructive/10 text-destructive px-4 py-3 text-sm" role="alert">
            {error}
          </div>
        )}
        {tripByIdError && (
          <div className="rounded-md bg-destructive/10 text-destructive px-4 py-3 text-sm" role="alert">
            {tripByIdError}
          </div>
        )}

        {tripById && !loadingTripById && (
          <Card>
            <CardHeader>
              <CardTitle>Trip details</CardTitle>
              <p className="text-sm text-muted-foreground">
                Trip: {tripById.name}
                {tripById.created_by && (
                  <span className="ml-2">Created by: <span className="font-mono text-xs">{tripById.created_by}</span></span>
                )}
              </p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {tripLookupList.map((trip) => {
                  const waypoints = waypointsByTripId[trip.id]
                  const meals = mealsByTripId[trip.id]
                  const announcements = announcementsByTripId[trip.id]
                  const gear = gearByTripId[trip.id]
                  const isExpanded = expandedTripId === trip.id
                  const isLoadingDetails = loadingDetailsTripId === trip.id
                  return (
                    <li key={trip.id} className="rounded-md border bg-card overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <button
                            type="button"
                            onClick={() => toggleTripDetails(trip.id)}
                            className="shrink-0 text-muted-foreground hover:text-foreground p-0.5"
                            aria-expanded={isExpanded}
                            title={isExpanded ? 'Hide details' : 'Show details'}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                          <div className="min-w-0">
                            <span className="font-medium">{trip.name}</span>
                            {trip.start_date && (
                              <span className="text-muted-foreground ml-2">
                                {trip.start_date}
                                {trip.end_date ? ` – ${trip.end_date}` : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {trip.archived_at && (
                            <Badge variant="secondary">Archived</Badge>
                          )}
                          <Link to={`/trips/${trip.id}`} className="text-primary hover:underline">
                            View
                          </Link>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="border-t bg-muted/30 px-3 py-2 text-sm space-y-4">
                          {isLoadingDetails ? (
                            <p className="text-muted-foreground">Loading trip details…</p>
                          ) : (
                            <>
                              <section>
                                <h4 className="font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                                  <MapPin className="h-3.5 w-3.5" /> Waypoints
                                </h4>
                                {waypoints && waypoints.length > 0 ? (
                                  <ul className="space-y-1.5">
                                    {waypoints.map((wp, idx) => (
                                      <li key={wp.id || idx} className="flex items-start gap-2 text-muted-foreground">
                                        <span>
                                          <span className="text-foreground font-medium">{wp.name}</span>
                                          {wp.trip_day != null && <span className="ml-1">Day {wp.trip_day}</span>}
                                          {wp.poi_type && (
                                            <Badge variant="outline" className="ml-1 text-xs capitalize">
                                              {wp.poi_type.replace('_', ' ')}
                                            </Badge>
                                          )}
                                          {(wp.latitude != null || wp.longitude != null) && (
                                            <span className="ml-1">
                                              ({wp.latitude?.toFixed(4)}, {wp.longitude?.toFixed(4)})
                                            </span>
                                          )}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-muted-foreground text-xs">No waypoints.</p>
                                )}
                              </section>
                              <section>
                                <h4 className="font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                                  <UtensilsCrossed className="h-3.5 w-3.5" /> Meals
                                </h4>
                                {meals && meals.length > 0 ? (
                                  <ul className="space-y-1.5">
                                    {meals.map((m) => (
                                      <li key={m.id} className="text-muted-foreground">
                                        <span className="text-foreground font-medium">{m.name}</span>
                                        {m.planned_date && <span className="ml-1">{m.planned_date}</span>}
                                        {m.planned_meal_time && (
                                          <Badge variant="outline" className="ml-1 text-xs capitalize">
                                            {m.planned_meal_time}
                                          </Badge>
                                        )}
                                        {m.meal_type && (
                                          <span className="ml-1 text-xs">({m.meal_type})</span>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-muted-foreground text-xs">No meals.</p>
                                )}
                              </section>
                              <section>
                                <h4 className="font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                                  <Megaphone className="h-3.5 w-3.5" /> Announcements
                                </h4>
                                {announcements && announcements.length > 0 ? (
                                  <ul className="space-y-1.5">
                                    {announcements.map((a) => (
                                      <li key={a.id} className="text-muted-foreground">
                                        <p className="text-foreground">{a.content}</p>
                                        <span className="text-xs">{new Date(a.created_at).toLocaleString()}</span>
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-muted-foreground text-xs">No announcements.</p>
                                )}
                              </section>
                              <section>
                                <h4 className="font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                                  <Backpack className="h-3.5 w-3.5" /> Gear
                                </h4>
                                {gear && gear.length > 0 ? (
                                  <ul className="space-y-1.5">
                                    {gear.map((g) => (
                                      <li key={g.id} className="text-muted-foreground">
                                        <span className="text-foreground font-medium">{g.name}</span>
                                        {g.category && (
                                          <Badge variant="outline" className="ml-1 text-xs capitalize">
                                            {String(g.category).replace('_', ' ')}
                                          </Badge>
                                        )}
                                        {g.quantity != null && g.quantity > 1 && (
                                          <span className="ml-1">×{g.quantity}</span>
                                        )}
                                        {g.notes && <span className="ml-1 text-xs">— {g.notes}</span>}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-muted-foreground text-xs">No gear.</p>
                                )}
                              </section>
                            </>
                          )}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </CardContent>
          </Card>
        )}

        {targetUserId && !loading && (
          <>
            {targetUserInfo && (
              <Card>
                <CardHeader>
                  <CardTitle>User</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {targetUserInfo.full_name && (
                      <span className="font-medium text-foreground">{targetUserInfo.full_name}</span>
                    )}
                    {targetUserInfo.full_name && targetUserInfo.email && ' · '}
                    {targetUserInfo.email && (
                      <a
                        href={`mailto:${targetUserInfo.email}`}
                        className="text-primary hover:underline"
                      >
                        {targetUserInfo.email}
                      </a>
                    )}
                    {(!targetUserInfo.full_name && !targetUserInfo.email) && (
                      <span className="text-muted-foreground">No name or email</span>
                    )}
                  </p>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    <span className="text-muted-foreground">ID</span>
                    <span className="font-mono text-xs break-all">{targetUserInfo.id}</span>
                  </div>
                  {targetUserInfo.created_at && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <span className="text-muted-foreground">Created</span>
                      <span>{new Date(targetUserInfo.created_at).toLocaleString()}</span>
                    </div>
                  )}
                  {targetUserInfo.profile && (
                    <>
                      {targetUserInfo.profile.location && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          <span className="text-muted-foreground">Location</span>
                          <span>{targetUserInfo.profile.location}</span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <span className="text-muted-foreground">Experience</span>
                        <span className="capitalize">{targetUserInfo.profile.experience_level.replace('_', ' ')}</span>
                      </div>
                      {targetUserInfo.profile.years_overlanding > 0 && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          <span className="text-muted-foreground">Years overlanding</span>
                          <span>{targetUserInfo.profile.years_overlanding}</span>
                        </div>
                      )}
                      {targetUserInfo.profile.bio && (
                        <div className="pt-1">
                          <span className="text-muted-foreground block mb-0.5">Bio</span>
                          <p className="text-foreground">{targetUserInfo.profile.bio}</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Subscription</CardTitle>
                {subscription && (
                  <p className="text-sm text-muted-foreground">
                    Current tier: <strong className="capitalize">{subscription.tier}</strong>
                    {' · '}
                    Trip limit: {subscription.limits.tripLimit === -1 ? 'Unlimited' : subscription.limits.tripLimit}
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {subscription ? (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <Label className="sr-only">Change tier</Label>
                      <Select
                        value={subscription.tier}
                        onValueChange={(v) => handleTierChange(v as SubscriptionTier)}
                        disabled={updatingTier}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                        </SelectContent>
                      </Select>
                      {updatingTier && <span className="text-sm text-muted-foreground">Updating…</span>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(['free', 'pro'] as const).map((t) => (
                        <Badge
                          key={t}
                          variant={subscription.tier === t ? 'default' : 'secondary'}
                          className="capitalize"
                        >
                          {t}
                        </Badge>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">No subscription data for this user.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Trips</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {trips.length} trip{trips.length !== 1 ? 's' : ''} for this user.
                </p>
              </CardHeader>
              <CardContent>
                {trips.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No trips found.</p>
                ) : (
                  <ul className="space-y-2">
                    {trips.map((trip) => {
                      const waypoints = waypointsByTripId[trip.id]
                      const meals = mealsByTripId[trip.id]
                      const announcements = announcementsByTripId[trip.id]
                      const gear = gearByTripId[trip.id]
                      const isExpanded = expandedTripId === trip.id
                      const isLoadingDetails = loadingDetailsTripId === trip.id
                      return (
                        <li key={trip.id} className="rounded-md border bg-card overflow-hidden">
                          <div className="flex items-center justify-between px-3 py-2 text-sm">
                            <div className="flex items-center gap-2 min-w-0">
                              <button
                                type="button"
                                onClick={() => toggleTripDetails(trip.id)}
                                className="shrink-0 text-muted-foreground hover:text-foreground p-0.5"
                                aria-expanded={isExpanded}
                                title={isExpanded ? 'Hide details' : 'Show details'}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                              <div className="min-w-0">
                                <span className="font-medium">{trip.name}</span>
                                {trip.start_date && (
                                  <span className="text-muted-foreground ml-2">
                                    {trip.start_date}
                                    {trip.end_date ? ` – ${trip.end_date}` : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {trip.archived_at && (
                                <Badge variant="secondary">Archived</Badge>
                              )}
                              <Link to={`/trips/${trip.id}`} className="text-primary hover:underline">
                                View
                              </Link>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="border-t bg-muted/30 px-3 py-2 text-sm space-y-4">
                              {isLoadingDetails ? (
                                <p className="text-muted-foreground">Loading trip details…</p>
                              ) : (
                                <>
                                  <section>
                                    <h4 className="font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                                      <MapPin className="h-3.5 w-3.5" /> Waypoints
                                    </h4>
                                    {waypoints && waypoints.length > 0 ? (
                                      <ul className="space-y-1.5">
                                        {waypoints.map((wp, idx) => (
                                          <li key={wp.id || idx} className="flex items-start gap-2 text-muted-foreground">
                                            <span>
                                              <span className="text-foreground font-medium">{wp.name}</span>
                                              {wp.trip_day != null && <span className="ml-1">Day {wp.trip_day}</span>}
                                              {wp.poi_type && (
                                                <Badge variant="outline" className="ml-1 text-xs capitalize">
                                                  {wp.poi_type.replace('_', ' ')}
                                                </Badge>
                                              )}
                                              {(wp.latitude != null || wp.longitude != null) && (
                                                <span className="ml-1">
                                                  ({wp.latitude?.toFixed(4)}, {wp.longitude?.toFixed(4)})
                                                </span>
                                              )}
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="text-muted-foreground text-xs">No waypoints.</p>
                                    )}
                                  </section>
                                  <section>
                                    <h4 className="font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                                      <UtensilsCrossed className="h-3.5 w-3.5" /> Meals
                                    </h4>
                                    {meals && meals.length > 0 ? (
                                      <ul className="space-y-1.5">
                                        {meals.map((m) => (
                                          <li key={m.id} className="text-muted-foreground">
                                            <span className="text-foreground font-medium">{m.name}</span>
                                            {m.planned_date && <span className="ml-1">{m.planned_date}</span>}
                                            {m.planned_meal_time && (
                                              <Badge variant="outline" className="ml-1 text-xs capitalize">
                                                {m.planned_meal_time}
                                              </Badge>
                                            )}
                                            {m.meal_type && (
                                              <span className="ml-1 text-xs">({m.meal_type})</span>
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="text-muted-foreground text-xs">No meals.</p>
                                    )}
                                  </section>
                                  <section>
                                    <h4 className="font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                                      <Megaphone className="h-3.5 w-3.5" /> Announcements
                                    </h4>
                                    {announcements && announcements.length > 0 ? (
                                      <ul className="space-y-1.5">
                                        {announcements.map((a) => (
                                          <li key={a.id} className="text-muted-foreground">
                                            <p className="text-foreground">{a.content}</p>
                                            <span className="text-xs">{new Date(a.created_at).toLocaleString()}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="text-muted-foreground text-xs">No announcements.</p>
                                    )}
                                  </section>
                                  <section>
                                    <h4 className="font-medium text-foreground mb-1.5 flex items-center gap-1.5">
                                      <Backpack className="h-3.5 w-3.5" /> Gear
                                    </h4>
                                    {gear && gear.length > 0 ? (
                                      <ul className="space-y-1.5">
                                        {gear.map((g) => (
                                          <li key={g.id} className="text-muted-foreground">
                                            <span className="text-foreground font-medium">{g.name}</span>
                                            {g.category && (
                                              <Badge variant="outline" className="ml-1 text-xs capitalize">
                                                {String(g.category).replace('_', ' ')}
                                              </Badge>
                                            )}
                                            {g.quantity != null && g.quantity > 1 && (
                                              <span className="ml-1">×{g.quantity}</span>
                                            )}
                                            {g.notes && <span className="ml-1 text-xs">— {g.notes}</span>}
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="text-muted-foreground text-xs">No gear.</p>
                                    )}
                                  </section>
                                </>
                              )}
                            </div>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PageLayout>
  )
}
